/*
 * Кнопка-иконка — действие без подписи.
 *
 * 48 безподписных круглых кнопок кита собираются двумя осями классов: variant (громкость
 * подложки) × tone (последствие действия) плюс состояние selected («включено»). Разбор
 * вхождений — в docs/ui-kit/contracts/icon-button.md. Итоговые цвета складываются локальными
 * переменными --ib-*, справа от каждой стоит токен, а не значение.
 *
 * Порядок правил: базовое → размер → вариант → тон → части → selected → focus-visible →
 * loading → disabled → hover → active. Ховер и нажатие стоят последними и закрыты
 * :not(:disabled):not(.loading) — так они не спорят с выключенностью и загрузкой ни
 * специфичностью, ни источником, а специфичность по файлу только растёт.
 *
 * Материал. Стекло несёт ровно один вариант — secondary: кит рисует круглую кнопку
 * rgba(255,255,255,0.78) с blur(12px) saturate(170%) поверх фотографии товара (UI Kit 1083).
 * Остальные три материалом не являются: primary — сплошная тональная заливка, subtle —
 * приглушённая плашка по туши (крестик модалки, очистка поля), ghost — вообще без подложки.
 * У subtle размытия нет и по второму счёту: именно им набирают кебаб строки и трейлинг
 * ListRow, а двести backdrop-filter на экран кладут WebView (правило 14).
 * Разбор с цифрами — docs/ui-kit/contracts/icon-button.md, раздел «Материал».
 *
 * Круг на стеклянной полосе (слот FloatingActionBar, TableToolbar, ряд действий FilterPanel,
 * AppTopBar) своего размытия не несёт: полоса объявляет --control-blur: none, наследование
 * доносит его до слота. Заливка, hairline и тень остаются — кит рисует именно так
 * (Data Grid 274: капсулы полосы идут без backdrop-filter). Механизм описан в tokens.css
 * рядом с лестницей --blur-*.
 *
 * Медиазапросов по ширине нет: круг одинаков на 360px и на десктопе, плотность выбирается
 * пропом size. Перенос ряда кнопок — забота контейнера.
 */

.root {
  --ib-size: var(--control-h-sm);
  --ib-bg: var(--color-surface);
  --ib-fg: var(--color-text-secondary);
  --ib-border: var(--color-border-strong);
  --ib-shadow: var(--shadow-control);
  --ib-ring: 0 0 0 0 transparent;
  --ib-ring-focus: var(--ring-focus);

  /* Размывает только стеклянный вариант, остальные оставляют none — см. шапку файла */
  --ib-blur: none;

  position: relative;
  display: inline-grid;
  flex: 0 0 auto;
  place-items: center;

  /* Круг не сжимается и не растягивается: глиф обязан остаться по центру */
  width: var(--ib-size);
  height: var(--ib-size);
  padding: 0;
  color: var(--ib-fg);
  cursor: pointer;
  background: var(--ib-bg);
  border: var(--border-hairline) solid var(--ib-border);
  border-radius: var(--radius-pill);
  box-shadow: var(--ib-ring), var(--ib-shadow);

  /* -webkit- обязателен рядом: планка Safari 15.4, см. ARCHITECTURE.md §19 */
  -webkit-backdrop-filter: var(--ib-blur);
  backdrop-filter: var(--ib-blur);
  transition:
    color var(--transition-fast),
    border-color var(--transition-fast),
    box-shadow var(--transition-fast),
    transform var(--transition-fast);
}

/* Шкала: кит даёт 24…44, решение Q4 сводит их к четырём ступеням — тем же, что у Button */

.xs {
  --ib-size: var(--control-h-xs);
}

.sm {
  --ib-size: var(--control-h-sm);
}

.md {
  --ib-size: var(--control-h-md);
}

.lg {
  --ib-size: var(--control-h-lg);
}

/* Вариант и тон покоя */

.primary {
  --ib-bg: var(--gradient-accent);
  --ib-fg: var(--color-accent-text-on-solid);
  --ib-border: var(--color-accent-edge);
  --ib-shadow: var(--shadow-accent);
}

/*
 * Единственное стекло среди вариантов. --gloss-rim добавлен сверх кита: он обводит круг
 * целиком, и без него в тёмной теме (7 % белого) у стекла не видно толщины.
 * Размытие берётся --blur-control (12px), а не --blur-control-lg (20px): 20px кит рисует
 * только у плавающей кнопки, которая висит над содержимым (UI Kit 2113, у нас ScrollToTop),
 * а кнопка в потоке лежит на панели и размывает её же — см. лестницу в tokens.css.
 */
.secondary {
  /* На стеклянной полосе размытие гасится её --control-blur, вне полосы работает запасное */
  --ib-blur: var(--control-blur, var(--blur-control));
  --ib-bg: var(--glass-control);
  --ib-fg: var(--color-text-secondary);
  --ib-border: var(--color-border-strong);
  --ib-shadow: var(--gloss-rim), var(--shadow-control);
}

/* Приглушённая плашка: крестик модалки, очистка поля, стрелки календаря. Рамки в ките нет */
.subtle {
  --ib-bg: var(--color-neutral-tint);
  --ib-fg: var(--color-text-secondary);
  --ib-border: transparent;
  --ib-shadow: 0 0 0 0 transparent;
}

.ghost {
  --ib-bg: transparent;
  --ib-fg: var(--color-text-secondary);
  --ib-border: transparent;
  --ib-shadow: 0 0 0 0 transparent;
}

/* Кольцо фокуса у необратимого действия красное: синий рядом с красным откликом чужой */
.toneDanger {
  --ib-ring-focus: var(--ring-error);
}

/*
 * Заливка — единственный вариант, где тон виден в покое: полутонов у сплошного круга нет.
 * Цветного «подъёма» под красной заливкой в системе нет (у акцента это --shadow-accent):
 * берётся верхний блик — ровно тот inset, что нарисован в ките.
 */
.primary.toneDanger {
  --ib-bg: var(--gradient-danger);
  --ib-fg: var(--color-danger-text-on-solid);
  --ib-border: var(--color-danger-edge);
  --ib-shadow: var(--gloss-top-on-accent);
}

/*
 * Глиф лежит в собственной ячейке грида: спиннер при загрузке встаёт на его место,
 * поэтому геометрия круга не меняется.
 */
.glyph {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/*
 * Счётчик над правым верхним углом. Якорь по левому краю плюс сдвиг трансформом: капсула
 * растёт симметрично, и подгонять смещение под «99+» не нужно — кит правит right с −3 на −8.
 * Рецепт из docs/ui-kit/contracts/counter-badge.md.
 */
.badge {
  position: absolute;
  top: 0;
  left: 100%;
  transform: translate(-60%, -35%);
}

/* Включено: в избранном, в сравнении. Единственная роль акцентного тинта у иконочной кнопки */
.root.selected {
  --ib-bg: var(--color-accent-tint);
  --ib-fg: var(--color-accent-text);
  --ib-border: var(--color-accent-border);
  --ib-shadow: var(--gloss-top);
}

/* Кит фокус только описывает подписью «focus ring 3.5px» — здесь он нарисован */
.root:focus-visible {
  --ib-ring: var(--ib-ring-focus);

  outline: none;
}

/* Идёт запрос: подложка не меняется, состояние несут спиннер, курсор и aria-busy */
.loading {
  cursor: progress;
}

/*
 * Выключено выглядит одинаково у всех подложек: кит рисует три разных погашенных фона,
 * но «выключено» не должно зависеть от того, какой кнопка была.
 */
.root:disabled {
  --ib-bg: var(--color-neutral-tint);
  --ib-fg: var(--color-text-disabled);
  --ib-border: var(--color-border-subtle);
  --ib-shadow: var(--gloss-top);
  --ib-ring: 0 0 0 0 transparent;

  /* Выключенный круг перестаёт быть материалом: кит гасит его плоским тинтом */
  --ib-blur: none;

  cursor: default;
}

.ghost:disabled {
  --ib-bg: transparent;
  --ib-border: transparent;
  --ib-shadow: 0 0 0 0 transparent;
}

/* Ховер только на устройствах с курсором: на тач-экране он залипает */
@media (hover: hover) {
  /* Отклик стекла — не смена цвета, а уплотнение: кит уводит 0.78 в 0.95 (UI Kit 1083) */
  .secondary:not(:disabled):not(.loading):hover {
    --ib-bg: var(--glass-control-hover);
    --ib-fg: var(--color-accent-text-hover);
    --ib-border: var(--color-accent-border);
  }

  .primary:not(:disabled):not(.loading):hover {
    --ib-bg: var(--gradient-accent-hover);
  }

  /* Отклик приглушённой плашки нейтральный: «закрыть» и «очистить» синеть незачем */
  .subtle:not(:disabled):not(.loading):hover {
    --ib-bg: var(--color-neutral-tint-strong);
    --ib-fg: var(--color-text);
  }

  .ghost:not(:disabled):not(.loading):hover {
    --ib-bg: var(--color-accent-tint-weak);
    --ib-fg: var(--color-accent-text-hover);
  }

  .root.selected:not(:disabled):not(.loading):hover {
    --ib-bg: var(--color-accent-tint-strong);
    --ib-fg: var(--color-accent-text-hover);
  }

  .primary.toneDanger:not(:disabled):not(.loading):hover {
    --ib-bg: var(--gradient-danger-hover);
  }

  /* Красным иконочная кнопка становится под курсором, а не в покое: см. контракт */
  .secondary.toneDanger:not(:disabled):not(.loading):hover {
    --ib-bg: var(--glass-control-hover);
    --ib-fg: var(--color-danger-text);
    --ib-border: var(--color-danger-border);
  }

  /* Красный глиф на красном тинте всегда -strong: правило css-tokens-theming */
  .subtle.toneDanger:not(:disabled):not(.loading):hover,
  .ghost.toneDanger:not(:disabled):not(.loading):hover {
    --ib-bg: var(--color-danger-tint);
    --ib-fg: var(--color-danger-text-strong);
    --ib-border: var(--color-danger-border);
  }
}

/*
 * Нажатие: механика кита — scale(0.98) плюс утопленная тень. У стекла showcase рисует
 * сплошную акцентную заливку: на круге 32px тинт почти не читается, подложки под него
 * там 32 пикселя, а не 120, как у кнопки с подписью.
 */

.root:not(:disabled):not(.loading):active {
  --ib-shadow: var(--shadow-inset-field);

  transform: scale(var(--press-scale));
}

/* Заливка нажатия непрозрачна, стекла за ней больше нет — размытие снимается вместе с ним */
.secondary:not(:disabled):not(.loading):active,
.root.selected:not(:disabled):not(.loading):active {
  --ib-blur: none;
  --ib-bg: var(--gradient-accent-pressed);
  --ib-fg: var(--color-accent-text-on-solid);
  --ib-border: var(--color-accent-edge);
}

.primary:not(:disabled):not(.loading):active {
  --ib-bg: var(--gradient-accent-pressed);
}

/* На тач-экране ховера нет: нажатие обязано показать отклик самостоятельно */
.ghost:not(:disabled):not(.loading):active {
  --ib-bg: var(--color-accent-tint-strong);
  --ib-fg: var(--color-accent-text-pressed);
}

/* Приглушённая плашка и под нажатием остаётся нейтральной — как её ховер */
.subtle:not(:disabled):not(.loading):active {
  --ib-bg: var(--color-neutral-tint-strong);
  --ib-fg: var(--color-text);
}

.primary.toneDanger:not(:disabled):not(.loading):active,
.secondary.toneDanger:not(:disabled):not(.loading):active {
  --ib-blur: none;
  --ib-bg: var(--gradient-danger-pressed);
  --ib-fg: var(--color-danger-text-on-solid);
  --ib-border: var(--color-danger-edge);
}

.subtle.toneDanger:not(:disabled):not(.loading):active,
.ghost.toneDanger:not(:disabled):not(.loading):active {
  --ib-bg: var(--color-danger-tint);
  --ib-fg: var(--color-danger-text-strong);
  --ib-border: var(--color-danger-border-focus);
}

/*
 * Тач-цель. 45 из 48 вхождений кита ниже 44px — самый острый случай Q5.
 * Конвенция слайса (min-height) здесь не годится: кнопка квадратная, и рост высоты превратил
 * бы круг в капсулу, а рост обеих сторон вытолкнул бы соседей — кебаб поднял бы высоту
 * виртуализированной строки грида, крестик очистки упёрся бы в рамку поля.
 * Поэтому геометрия остаётся, а расширяется невидимая зона нажатия — тем же приёмом,
 * что у крестика Chip. any-pointer: на гибридах основной указатель мышь, но палец никуда
 * не делся.
 */
@media (any-pointer: coarse) {
  .root::after {
    position: absolute;
    top: 50%;
    left: 50%;
    width: max(100%, var(--control-h-lg));
    height: max(100%, var(--control-h-lg));
    transform: translate(-50%, -50%);
    content: '';
  }
}

/*
 * В режиме высокой контрастности Windows заливки, тинты и кольца сбрасываются принудительно:
 * круг держится на рамке, а тон вместе с цветом теряется — включённость отмечается
 * системным Highlight, иначе «в избранном» неотличимо от «не в избранном».
 */
@media (forced-colors: active) {
  .root {
    /* Стекла в высококонтрастном режиме нет: заливку система задаёт сама, а размытие
       осталось бы поверх неё мутным пятном */
    --ib-blur: none;

    border-color: CanvasText;
  }

  .root.selected {
    border-color: Highlight;
    border-style: double;
  }

  /* box-shadow в HCM гасится спекой — кольцо фокуса заменяется системным outline */
  .root:focus-visible {
    outline: 2px solid Highlight;
    outline-offset: 2px;
  }

  .root:disabled {
    color: GrayText;
    border-color: GrayText;
  }
}
